import * as qs from 'qs'
import _ from 'lodash'
import type Passport from '@patsnap-utils/passport/src/passport'
import { CommonApis } from './apis/common.api'
import type { HeaderService } from '@patsnap-utils/biz'
import { NeedAddTrialActionType, type IAddTrialExtendBody } from './apis/common.api.types'
import { isAxiosError } from './exception'
import { Site, SLD } from '@patsnap-utils/biz';
import { addTrialDeferred } from '@eureka/utils/account'
import { InitCookie } from '@patsnap/init-cookie'
import { UsageAmountWhere } from '@eureka/shared/edition-upgrade/src/constants'
import { checkAndClaimCredits } from './credits-charge-service'

const MAX_TRIAL_STORAGE_KEY = 'ADDTRIAL_clickcount'
const MAX_TRIAL_STORAGE_COUNT = 5
const PRIVILEGE_DENIED_ERROR_CODE = 90112055

function redirectTo403Page() {
    if (typeof window === 'undefined') return
    window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href
}

function redirectToAccountStatusPage() {
    if (typeof window === 'undefined') return
    const pathName = new Site({ sld: SLD.EUREKA, pathname: 'account-status/' }).href;
    window.location.href = `${pathName}?account_status_from=${encodeURIComponent(window.location.href)}`
}

function redirectToGetStartedPage() {
    if (typeof window === 'undefined') return
    const next = encodeURIComponent(window.location.href)
    const pathName = new Site({ sld: SLD.EUREKA, pathname: 'onboarding/' }).href
    window.location.href = `${pathName}?next=${next}`
}

function getTrialCounts() {
    return Number(sessionStorage.getItem(MAX_TRIAL_STORAGE_KEY) || 0)
}

function increaseTrialCounts() {
    sessionStorage.setItem(MAX_TRIAL_STORAGE_KEY, `${getTrialCounts() + 1}`)
}

function getErrorCode(error: unknown) {
    return _.get(error, 'data.error_code') ||
        _.get(error, 'response.data.error_code') ||
        _.get(error, 'cause.error_code') ||
        _.get(error, 'error_code')
}

function isPrivilegeDeniedErrorCode(errorCode: unknown) {
    return Number(errorCode) === PRIVILEGE_DENIED_ERROR_CODE
}

class AddTrialService {
    passport: Passport | void = undefined

    ENABLE_AUTO_ADD_TRIAL = true;
    async addTrial(headerService: HeaderService, passport: Passport | void, agentName?: UsageAmountWhere) {
        try {
            this.passport = passport
            const grantedProductIds: string[] | undefined = headerService.account?._info.granted_product_ids
            if (grantedProductIds === undefined) {
                throw new Error('Granted_product_ids is undefined!')
            }
            const extendBody = this.getAddTrialExtendBody(agentName)
            const empowerType = this.getEmpowerType(extendBody.from || (extendBody as any).start_from);

            const needAddTrialData = await CommonApis.needAddTrial(empowerType || '', extendBody.from, extendBody);
            if (isPrivilegeDeniedErrorCode(_.get(needAddTrialData, 'error_code'))) {
                redirectTo403Page()
                return;
            }
            const actionType = _.get(needAddTrialData, 'data.action_type', '') as NeedAddTrialActionType | string;
            this.ENABLE_AUTO_ADD_TRIAL = false;

            switch (actionType) {
            case NeedAddTrialActionType.DIRECT_LOGIN:
                await checkAndClaimCredits()
                break;
            case NeedAddTrialActionType.GRANT_INDIVIDUAL_TENANT_PRIVILEGE:
                if (getTrialCounts() >= MAX_TRIAL_STORAGE_COUNT) {
                    sessionStorage.removeItem(MAX_TRIAL_STORAGE_KEY)
                    redirectTo403Page()
                    throw new Error(`Trial counts is over ${MAX_TRIAL_STORAGE_COUNT}!`);
                }
                localStorage.removeItem('patsnap_header_account_info')
                console.warn('[AddTrial]', '赋权成功，刷新页面生效')
                this.passport?.removeLocal()
                increaseTrialCounts()
                window.location.reload()
                break;
            case NeedAddTrialActionType.NEW_USER_ONBOARDING:
                redirectToGetStartedPage()
                break;
            case NeedAddTrialActionType.CONTACT_ADMIN:
                redirectToAccountStatusPage()
                break;
            default:
                redirectToAccountStatusPage()
                break;
            }
        } catch (error) {
            if (isPrivilegeDeniedErrorCode(getErrorCode(error))) {
                redirectTo403Page()
                return;
            }
            console.error('[AddTrial]', error)
        } finally {
            this.ENABLE_AUTO_ADD_TRIAL = false
            addTrialDeferred.resolve(true);
        }
    }

    isMobileDevice() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        );
    }

    getProductSource(agentName?: UsageAmountWhere) {
        const hashQuery = window.location.hash.split('?')[1] || '';
        const params = new URLSearchParams(hashQuery || window.location.search);
        const type = params.get('type') || agentName || UsageAmountWhere.TRIZ;
        return type.startsWith('rd_') ? type : `rd_${type}`;
    }

    getCookieExtendBody(agentName?: UsageAmountWhere) {
        return {
            source: InitCookie.getCampaignName(),
            entry_channel: InitCookie.getEntryChannel() || 'rd_triz',
            product_source: this.getProductSource(agentName),
        };
    }

    getAddTrialExtendBody(agentName?: UsageAmountWhere): IAddTrialExtendBody {
        const hash = window.location.hash;
        const queryString = hash.includes('?') ? '?' + hash.split('?')[1] : window.location.search;

        const query = qs.parse(queryString, {
            ignoreQueryPrefix: true,
        })
        // warning: 直接全部给后端可能会带有多余的参数
        // @ts-ignore
        return {
            from: InitCookie.getCampaignName(),
            ...query,
            ...this.getCookieExtendBody(agentName),
            media_type: this.isMobileDevice() ? 'Mobile' : '',
        }
    }

    getEmpowerType(from?: string) {
        const SEO_SOURCES = [
            'eureka-seo-report-detail',
            'eureka-seo-report-list',
            'eureka-seo-article',
            'eureka-seo-insight',
            'discovery_seo_company'
        ];
        if (from && SEO_SOURCES.includes(from)) return 'RD_SEO';
        return ''
    }
}

export const addTrialService = new AddTrialService()

export function errorHandleIgnore403(e: Error) {
    return isAxiosError(e) && e.response?.status === 403;
}
